import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getSections, type Section } from './constants'
import SuggestionTextSearch from './SuggestionTextSearch'
import { urlParamsUtils } from './utils'

// クエリパラメータを正しく引き継ぎつつ、keyごとに値をカンマ区切りでまとめる
const setTagsToUrl = (sectionId: string, keyValuePairs: string[], sections: Section[]) => {
  const params = urlParamsUtils.getCurrentParams()
  const section = sections.find((s) => s.id === sectionId)
  if (!section) return

  // 1. すべてのkeyごとに、全セクションの値を集める
  const keyToValues = new Map<string, Set<string>>()
  for (const [key, val] of params.entries()) {
    if (!keyToValues.has(key)) keyToValues.set(key, new Set())
    val.split(',').forEach((v) => keyToValues.get(key)!.add(v))
  }

  // 2. 今回のセクションのkeyごとに、値を上書き
  section.tags.forEach((tag) => {
    const set = keyToValues.get(tag.key) || new Set<string>()
    // このセクションのタグ値を全て消す
    section.tags.filter((t) => t.key === tag.key).forEach((t) => set.delete(t.value))
    keyToValues.set(tag.key, set)
  })

  // 3. 選択されたkey:valueペアから、該当するkeyに値を追加
  keyValuePairs.forEach((keyValuePair) => {
    const [key, value] = keyValuePair.split(':')
    const set = keyToValues.get(key) || new Set<string>()
    set.add(value)
    keyToValues.set(key, set)
  })

  // 4. paramsを更新
  const keepKeys: string[] = []
  params.forEach((_, key) => {
    if (!keyToValues.has(key)) keepKeys.push(key)
  })

  // フィルター関連以外のパラメータを保持
  const updates: Record<string, string | null> = {}
  params.forEach((value, key) => {
    if (!keepKeys.includes(key)) {
      updates[key] = null
    }
  })

  keyToValues.forEach((set, key) => {
    if (set.size > 0) {
      updates[key] = Array.from(set).join(',')
    } else {
      updates[key] = null
    }
  })

  urlParamsUtils.updateParams(params, updates)
  urlParamsUtils.resetPageNumber(params)
  urlParamsUtils.navigateWithParams(params)
}

const TagButton = ({ label, checked, onClick }) => (
  <span
    className={`flex items-center border rounded-full px-4 py-2 mr-2 mb-2 bg-white ${
      checked ? 'border-green text-green' : 'border-gray-300 text-black'
    }`}
    style={{ fontSize: 12, lineHeight: '18px', fontWeight: 'normal' }}
  >
    <button
      type="button"
      className="flex items-center focus:outline-hidden"
      onClick={onClick}
      style={{ background: 'none', border: 'none', padding: 0, marginRight: 0 }}
    >
      <span className="font-noto">{label}</span>
    </button>
  </span>
)

const SectionAccordion = ({ section, selected, onToggleTag, open, onToggleOpen, sectionRef, children = null }) => {
  const contentRef = useRef(null)
  const userInitiatedOpen = useRef(false)

  useEffect(() => {
    if (open && userInitiatedOpen.current) {
      userInitiatedOpen.current = false
    }
  }, [open])

  const handleUserToggleOpen = () => {
    userInitiatedOpen.current = true
    onToggleOpen()
  }

  return (
    <div className="mb-4" ref={sectionRef}>
      <button
        className="w-full flex justify-between items-center mb-2 text-left text-gray-800 bg-white hover:bg-gray-50 font-size-14 font-normal"
        onClick={handleUserToggleOpen}
        type="button"
      >
        <span>{section.label}</span>
        <img
          src={open ? '/images/icon/arrow_up.svg' : '/images/icon/arrow_down.svg'}
          alt={open ? '閉じる' : '開く'}
          width={24}
          height={24}
        />
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: open ? contentRef.current?.scrollHeight || '1000px' : 0,
          overflow: open ? 'visible' : 'hidden',
          transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div>
          {children || (
            <div className="flex flex-wrap">
              {section.tags.map((tag) => {
                const keyValue = `${tag.key}:${tag.value}`
                return (
                  <TagButton
                    key={`${section.id}-${tag.key}-${tag.value}`}
                    label={tag.label}
                    checked={selected.includes(keyValue)}
                    onClick={() => onToggleTag(keyValue, !selected.includes(keyValue), tag.key)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const AlwaysOpenSection = ({ section, selected, onToggleTag }) => (
  <div className="mb-4">
    <div className="mb-2 text-left text-gray-800 bg-white" style={{ fontSize: 14, fontWeight: 'normal' }}>
      {section.label}
    </div>
    <div>
      <div className="flex flex-wrap">
        {section.tags.map((tag) => {
          const keyValue = `${tag.key}:${tag.value}`
          return (
            <TagButton
              key={`${section.id}-${tag.key}-${tag.value}`}
              label={tag.label}
              checked={selected.includes(keyValue)}
              onClick={() => onToggleTag(keyValue, !selected.includes(keyValue), tag.key)}
            />
          )
        })}
      </div>
    </div>
  </div>
)

const DetailFilterPanel = () => {
  const { t } = useTranslation()
  const sections = React.useMemo(() => getSections(t), [t])

  const [openSections, setOpenSections] = useState(() => {
    const obj = {}
    sections.forEach((s) => {
      if (!s.alwaysOpen) {
        if (s.id === 'text_search') {
          const params = new URLSearchParams(window.location.search)
          obj[s.id] = !!params.get('text_search')
        } else {
          const tags = urlParamsUtils.getTagsFromUrl(s.id, sections)
          obj[s.id] = tags.length > 0
        }
      }
    })
    return obj
  })
  const [selected, setSelected] = useState(() => {
    const obj = {}
    sections.forEach((s) => {
      obj[s.id] = urlParamsUtils.getTagsFromUrl(s.id, sections)
    })
    return obj
  })
  const sectionRefs = useRef({})
  sections.forEach((s) => {
    if (!sectionRefs.current[s.id]) sectionRefs.current[s.id] = React.createRef()
  })

  useEffect(() => {
    const onPopState = () => {
      setSelected((prev) => {
        const obj = { ...prev }
        sections.forEach((s) => {
          obj[s.id] = urlParamsUtils.getTagsFromUrl(s.id, sections)
        })
        return obj
      })
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [sections])

  const handleToggleTag = (sectionId: string, keyValue: string, checked: boolean, key: string) => {
    const section = sections.find((s) => s.id === sectionId)
    if (!section) return

    // 複数選択を許可するkeyを自動検出
    const keyCounts = new Map()
    sections.forEach((s) => {
      s.tags.forEach((tag) => {
        keyCounts.set(tag.key, (keyCounts.get(tag.key) || 0) + 1)
      })
    })
    const allowMultipleKeys = Array.from(keyCounts.entries())
      .filter(([_, count]) => count > 1)
      .map(([key, _]) => key)

    let newSelected = [...(selected[sectionId] || [])]
    if (checked) {
      if (allowMultipleKeys.includes(key)) {
        // 複数選択を許可するkeyの場合は、既存の選択を保持して新しい値を追加
        if (!newSelected.includes(keyValue)) {
          newSelected.push(keyValue)
        }
      } else {
        // 単一選択のkeyの場合は、同じkeyを持つ既存の選択を解除
        newSelected = newSelected.filter((selectedKeyValue) => {
          const [selectedKey] = selectedKeyValue.split(':')
          return selectedKey !== key
        })
        // 新しい値を追加
        newSelected.push(keyValue)
      }
    } else {
      newSelected = newSelected.filter((v) => v !== keyValue)
    }
    setSelected((prev) => ({ ...prev, [sectionId]: newSelected }))
    setTagsToUrl(sectionId, newSelected, sections)
  }

  const handleToggleAccordion = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const [searchInput, setSearchInput] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('text_search') || ''
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setSearchInput(params.get('text_search') || '')
  }, [window.location.search])

  const handleTextSearch = (key: string, value: string, _type?: 'down' | 'up' | 'option' | 'text') => {
    if (key === 'text_search') {
      setSearchInput(value)
    }
  }

  const handleTextSubmit = (value: string = searchInput) => {
    const params = new URLSearchParams(window.location.search)
    if (value) {
      params.set('text_search', value)
    } else {
      params.delete('text_search')
    }
    if (params.get('page')) params.set('page', '1')
    window.location.href = params.toString() ? `?${params.toString()}` : '/'
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white">
      {sections.map((section) =>
        section.id === 'popular' ? null : section.alwaysOpen ? (
          <AlwaysOpenSection
            key={section.id}
            section={section}
            selected={selected[section.id] || []}
            onToggleTag={(keyValue, checked) => {
              const [key] = keyValue.split(':')
              handleToggleTag(section.id, keyValue, checked, key)
            }}
          />
        ) : section.id === 'text_search' ? (
          <SectionAccordion
            key={section.id}
            section={section}
            selected={[]}
            onToggleTag={() => {}}
            open={!!openSections[section.id]}
            onToggleOpen={() => handleToggleAccordion(section.id)}
            sectionRef={sectionRefs.current[section.id]}
          >
            <div className="flex items-center">
              <SuggestionTextSearch textSearch={searchInput} onChange={handleTextSearch} />
              <button
                onClick={() => handleTextSubmit()}
                className="ml-2 bg-green text-white px-4 py-2 rounded-md focus:outline-hidden"
              >
                検索
              </button>
            </div>
          </SectionAccordion>
        ) : (
          <SectionAccordion
            key={section.id}
            section={section}
            selected={selected[section.id] || []}
            onToggleTag={(keyValue, checked) => {
              const [key] = keyValue.split(':')
              handleToggleTag(section.id, keyValue, checked, key)
            }}
            open={!!openSections[section.id]}
            onToggleOpen={() => handleToggleAccordion(section.id)}
            sectionRef={sectionRefs.current[section.id]}
          />
        ),
      )}
    </div>
  )
}

export default DetailFilterPanel
